import React, { useState, useEffect, useRef } from 'react';
import { Invitation, SiteRegistrationForm } from '../../types';
import FormFieldSet from '../FormFieldSet';
import FloatingLabel from '../FloatingLabel';
import { InformationCircleIcon, ExclamationCircleIcon, CheckCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ExternalAuthorisation, Site } from '../../../../types';
import { doesManufacturerRequireManualAuth, getManufacturerPortalName } from '../../utils';
import { getCookie } from '../../../../utils';
import fuzzysort from 'fuzzysort';

interface ConnectStepProps {
    formData: SiteRegistrationForm;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    display?: boolean;
    authorised: boolean;
    connected: boolean;
    setConnected: (connected: boolean) => void;
    selectedExternalAuthorisation: ExternalAuthorisation | null;
    invitation?: Invitation | null;
    siteMatches?: { [siteId: number]: string };
    setSiteMatches?: (matches: { [siteId: number]: string }) => void;
}

interface ConnectFormProps {
    fieldKey: string;
    formData: SiteRegistrationForm;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    display?: boolean;
}

const ConnectForm = ({ fieldKey, formData, handleInputChange, display = false }: ConnectFormProps) => {
    if (display) {
        return (
            <div className={'bg-base-100 border rounded'}>
                <div>External Site ID: {formData[fieldKey as keyof SiteRegistrationForm] ? formData[fieldKey as keyof SiteRegistrationForm] : '-'}</div>
            </div>
        );
    } else {
        return (
            <input
                type="text"
                id={fieldKey}
                name={fieldKey}
                value={formData[fieldKey as keyof SiteRegistrationForm] ?? ''}
                onChange={handleInputChange}
                className="input focus:outline-none"
                placeholder="External Site ID"
            />
        );
    }
}

interface SearchableSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ id: string; name: string; address?: string; isDisabled?: boolean }>;
    placeholder?: string;
    targetSiteName?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ value, onChange, options, placeholder = "Search...", targetSiteName }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<{ id: string; name: string } | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Find the selected option details
    useEffect(() => {
        if (value) {
            const option = options.find(opt => opt.id === value);
            if (option) {
                setSelectedOption({ id: option.id, name: option.name });
            }
        } else {
            setSelectedOption(null);
        }
    }, [value, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Reset highlighted index when options change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [searchTerm]);

    // Rank (not filter) options based on fuzzy matching using fuzzysort
    const filteredOptions = (() => {
        // Determine what to rank by: user's search term or target site name
        const searchQuery = searchTerm.trim() || targetSiteName;
        
        if (!searchQuery) {
            // No search query and no target name, return all options as-is
            return options;
        }
        
        // Split search query into words for better matching
        const searchWords = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
        
        // Score each option based on all search words
        const scoredOptions = options.map(option => {
            let totalScore = 0;
            
            // Try to match each search word
            searchWords.forEach(word => {
                const result = fuzzysort.single(word, option.name);
                if (result) {
                    totalScore += result.score;
                }
            });
            
            const avgScore = searchWords.length > 0 ? totalScore / searchWords.length : 0;
            
            return {
                option,
                score: avgScore
            };
        });
        
        // Sort by score (highest first, less negative = better)
        scoredOptions.sort((a, b) => b.score - a.score);
        
        return scoredOptions.map(item => item.option);
    })();

    const handleSelect = (optionId: string) => {
        onChange(optionId);
        setSearchTerm('');
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange('');
        setSearchTerm('');
        setSelectedOption(null);
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                setIsOpen(true);
            }
            return;
        }

        const enabledOptions = filteredOptions.filter(opt => !opt.isDisabled);
        
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < enabledOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < enabledOptions.length) {
                    handleSelect(enabledOptions[highlightedIndex].id);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
        }
    };

    return (
        <div ref={wrapperRef} className="relative w-80">
            <div className="relative">
                <input
                    type="text"
                    className="input input-sm input-bordered w-full pr-8"
                    placeholder={selectedOption ? `${selectedOption.id} - ${selectedOption.name}` : placeholder}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => {
                        setIsOpen(true);
                        setHighlightedIndex(0);
                    }}
                    onKeyDown={handleKeyDown}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
                    {selectedOption && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
                </div>
            </div>
            
            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredOptions.map((option, idx) => {
                        const enabledOptions = filteredOptions.filter(opt => !opt.isDisabled);
                        const enabledIndex = option.isDisabled ? -1 : enabledOptions.findIndex(opt => opt.id === option.id);
                        const isHighlighted = enabledIndex === highlightedIndex;
                        
                        return (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => !option.isDisabled && handleSelect(option.id)}
                                disabled={option.isDisabled}
                                onMouseEnter={() => !option.isDisabled && setHighlightedIndex(enabledIndex)}
                                className={`
                                    w-full text-left px-3 py-2 transition-colors
                                    ${option.isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    ${isHighlighted ? 'bg-base-300' : 'hover:bg-base-200'}
                                    ${value === option.id ? 'bg-base-200' : ''}
                                `}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-semibold">{option.id}</span>
                                            {option.isDisabled && (
                                                <span className="badge badge-xs badge-ghost">Used</span>
                                            )}
                                        </div>
                                        <div className="text-sm">{option.name}</div>
                                        {option.address && (
                                            <div className="text-xs text-gray-500">{option.address}</div>
                                        )}
                                    </div>
                                    {value === option.id && (
                                        <CheckCircleIcon className="w-4 h-4 text-success flex-shrink-0" />
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
            
            {isOpen && searchTerm && filteredOptions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-3 text-center text-sm text-gray-500">
                    No matching sites found
                </div>
            )}
        </div>
    );
}

const ConnectStep: React.FC<ConnectStepProps> = ({ formData, handleInputChange, display = false, authorised, connected, setConnected, selectedExternalAuthorisation, invitation, siteMatches: externalSiteMatches, setSiteMatches: setExternalSiteMatches }) => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isFetchingExternalSites, setIsFetchingExternalSites] = useState(false);
    const [externalSites, setExternalSites] = useState<any[]>([]);
    const [localSiteMatches, setLocalSiteMatches] = useState<{ [siteId: number]: string }>({});
    
    // Use external state if provided, otherwise use local state
    const siteMatches = externalSiteMatches || localSiteMatches;
    const setSiteMatches = setExternalSiteMatches || setLocalSiteMatches;
    
    const hasPrefilled = invitation?.sites && invitation.sites.length > 0;

    // Fetch external sites from manufacturer portal
    useEffect(() => {
        if (authorised && selectedExternalAuthorisation && formData.manufacturer && hasPrefilled) {
            setIsFetchingExternalSites(true);
            fetch(invitation ? `/api/proxy_external_api_public/${invitation.token}/` : '/api/proxy_external_api/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken') || ''
                },
                body: JSON.stringify({
                    url: `/users/${selectedExternalAuthorisation.external_id}/installations`,
                    manufacturer: formData.manufacturer,
                    external_authorisation: selectedExternalAuthorisation,
                    method: 'GET'
                })
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 'OK' && data.data) {
                        setExternalSites(data.data);
                        // Auto-match sites by name
                        const matches: { [siteId: number]: string } = {};
                        invitation.sites.forEach(site => {
                            const match = data.data.find((ext: any) => 
                                ext.name?.toLowerCase().trim() === site.name.toLowerCase().trim()
                            );
                            if (match) {
                                matches[site.id] = String(match.idSite || match.id);
                            }
                        });
                        setSiteMatches(matches);
                    }
                })
                .catch(error => {
                    console.error('Failed to fetch external sites:', error);
                })
                .finally(() => {
                    setIsFetchingExternalSites(false);
                });
        }
    }, [authorised, selectedExternalAuthorisation, formData.manufacturer, hasPrefilled]);

    // Check if any sites are matched
    useEffect(() => {
        if (hasPrefilled && invitation?.sites) {
            const hasMatches = Object.keys(siteMatches).some(siteId => siteMatches[Number(siteId)]?.trim());
            setConnected(hasMatches);
        }
    }, [siteMatches, hasPrefilled, invitation?.sites]);

    const handleSiteMatchChange = (siteId: number, externalSiteId: string) => {
        const newMatches = {
            ...siteMatches,
            [siteId]: externalSiteId
        };
        setSiteMatches(newMatches);
    };

    const handleConnect = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!formData.external_site_id || !selectedExternalAuthorisation) {
            return;
        }
        setIsConnecting(true);
        fetch(invitation ? `/api/proxy_external_api_public/${invitation.token}/` : '/api/proxy_external_api/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken') || ''
            },
            body: JSON.stringify({
                url: `/users/${selectedExternalAuthorisation.external_id}/installations`,
                manufacturer: formData.manufacturer,
                external_authorisation: selectedExternalAuthorisation,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: {
                    username: selectedExternalAuthorisation.external_account,
                    idSite: formData.external_site_id
                }
            })
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 'OK' && data.data.idSite == formData.external_site_id) {
                    setConnected(true);
                } else {
                    setConnected(false);
                }
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setIsConnecting(false);
            });
    }

    const connectButtonDisabled = isConnecting || connected;

    return (
        <FormFieldSet legend="Connection">
            <div className="space-y-3">
                { formData.manufacturer && authorised
                    ? selectedExternalAuthorisation && <div className="flex flex-col items-start gap-3">
                        <div className="flex items-center gap-1">
                            <InformationCircleIcon className="w-4 h-4" />
                            {connected 
                                ? `All sites are connected to ${getManufacturerPortalName(formData.manufacturer)}.`
                                : `You have authorised with ${getManufacturerPortalName(formData.manufacturer)}.`
                            }
                        </div>
                        <ul className="list text-sm">
                            <li>External Manufacturer: {selectedExternalAuthorisation.manufacturer}</li>
                            <li>External ID: {selectedExternalAuthorisation.external_id}</li>
                            <li>External Account: {selectedExternalAuthorisation.external_account}</li>
                        </ul>

                        {hasPrefilled && invitation?.sites && (
                            <div className="w-full">
                                {isFetchingExternalSites ? (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Fetching sites from {getManufacturerPortalName(formData.manufacturer)}...
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <h4 className="font-semibold text-sm">Match Sites to {getManufacturerPortalName(formData.manufacturer)}</h4>
                                        <div className="space-y-2">
                                            {invitation.sites.map((site) => {
                                                const isMatched = Boolean(siteMatches[site.id] && String(siteMatches[site.id]).trim());
                                                
                                                // Prepare options for SearchableSelect
                                                const selectOptions = externalSites.map((extSite) => {
                                                    const siteId = String(extSite.idSite || extSite.id);
                                                    const isAlreadyUsed = Object.entries(siteMatches).some(
                                                        ([matchedSiteId, matchedExtId]) => 
                                                            matchedExtId === siteId && Number(matchedSiteId) !== site.id
                                                    );
                                                    return {
                                                        id: siteId,
                                                        name: extSite.name,
                                                        address: extSite.address,
                                                        isDisabled: isAlreadyUsed
                                                    };
                                                });
                                                
                                                return (
                                                    <div key={site.id} className="bg-base-100 rounded border border-base-300 p-3">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="font-medium text-sm">{site.name}</div>
                                                                    {isMatched && (
                                                                        <CheckCircleIcon className="w-4 h-4 text-success" />
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-gray-500">{site.address}</div>
                                                            </div>
                                                            <SearchableSelect
                                                                value={siteMatches[site.id] || ''}
                                                                onChange={(value) => handleSiteMatchChange(site.id, value)}
                                                                options={selectOptions}
                                                                placeholder="Search external sites..."
                                                                targetSiteName={site.name}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {externalSites.length > 0 && (
                                            <details className="collapse collapse-arrow bg-base-200">
                                                <summary className="collapse-title text-sm font-medium">
                                                    View all available sites from {getManufacturerPortalName(formData.manufacturer)} ({externalSites.length})
                                                </summary>
                                                <div className="collapse-content">
                                                    <div className="space-y-2 mt-2">
                                                        {externalSites.map((extSite, idx) => {
                                                            const siteId = String(extSite.idSite || extSite.id);
                                                            const isAlreadyUsed = Object.values(siteMatches).includes(siteId);
                                                            
                                                            return (
                                                                <div key={idx} className="flex items-center gap-3 p-3 bg-base-100 rounded border border-base-300">
                                                                    <div className="flex-1 space-y-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-mono text-sm font-semibold">{siteId}</span>
                                                                            {isAlreadyUsed && (
                                                                                <span className="badge badge-xs badge-success">Matched</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-sm">{extSite.name}</div>
                                                                        {extSite.address && (
                                                                            <div className="text-xs text-gray-500">{extSite.address}</div>
                                                                        )}
                                                                        {(extSite.gpsLatitude || extSite.gpsLongitude) && (
                                                                            <div className="text-xs font-mono text-gray-500">
                                                                                {extSite.gpsLatitude || '-'}, {extSite.gpsLongitude || '-'}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {!hasPrefilled && connected && (
                            <div className="text-sm">External Site ID: {formData.external_site_id}</div>
                        )}
                    </div>
                    : (
                        <div className="flex items-center gap-1">
                            <ExclamationCircleIcon className="w-4 h-4" />
                            { formData.manufacturer ? 'Please authorise with ' + formData.manufacturer + ' to continue.' : 'Please select a manufacturer to continue.' }
                        </div>
                    )
                }
                
                {!hasPrefilled && doesManufacturerRequireManualAuth(formData.manufacturer) && !connected && (
                    <div className="flex items-center gap-1">
                        <FloatingLabel className="w-1/2">
                            <span>External Site ID</span>
                            <ConnectForm fieldKey="external_site_id" formData={formData} handleInputChange={handleInputChange} display={display} />
                        </FloatingLabel>
                        <div className="tooltip" data-tip={`This is the site ID of your external system. It can be found in the external portal.`}>
                            <InformationCircleIcon className="w-4 h-4" />
                        </div>
                    </div>
                )}
                
                {!hasPrefilled && doesManufacturerRequireManualAuth(formData.manufacturer) && !connected && (
                    <button className='btn mt-2 btn-neutral' onClick={handleConnect} disabled={connectButtonDisabled}>
                        {isConnecting ? (
                                <div className="flex items-center">
                                    <span className="loading loading-dots loading-sm mr-2"></span>
                                    Connecting
                                </div>
                            ) : ( 'Connect' )
                        }
                    </button>
                )}
            </div>
        </FormFieldSet>
    );
};

export default ConnectStep;